import { request } from "../utils/api";

function buildQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean") {
      qs.set(key, value ? "true" : "false");
      return;
    }
    qs.set(key, String(value));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
}

async function unwrap(promise) {
  const res = await promise;
  if (res?.status === 1) return res.result;
  throw new Error(res?.message || "Request failed");
}

/** Agency Profiles */
export const listAgencies = (params) =>
  unwrap(request(`/libraries/agencies${buildQuery(params)}`));

export const getAgency = (id) =>
  unwrap(request(`/libraries/agencies/${id}`));

export const createAgency = (body) =>
  unwrap(
    request("/libraries/agencies", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );

export const updateAgency = (id, body) =>
  unwrap(
    request(`/libraries/agencies/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  );

export const deleteAgency = (id) =>
  unwrap(
    request(`/libraries/agencies/${id}`, {
      method: "DELETE",
    }),
  );

export const updateAgencyStatus = (id, status) =>
  unwrap(
    request(`/libraries/agencies/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  );

export const bulkUpdateAgencyStatus = (ids, status) =>
  unwrap(
    request("/libraries/agencies/status", {
      method: "PATCH",
      body: JSON.stringify({ ids, status }),
    }),
  );

/** Care Facilities */
export const listFacilities = (params) =>
  unwrap(request(`/libraries/facilities${buildQuery(params)}`));

export const getFacility = (id) =>
  unwrap(request(`/libraries/facilities/${id}`));

export const createFacility = (body) =>
  unwrap(
    request("/libraries/facilities", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );

export const updateFacility = (id, body) =>
  unwrap(
    request(`/libraries/facilities/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  );

export const deleteFacility = (id) =>
  unwrap(
    request(`/libraries/facilities/${id}`, {
      method: "DELETE",
    }),
  );

export const updateFacilityStatus = (id, status) =>
  unwrap(
    request(`/libraries/facilities/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  );

export const bulkUpdateFacilityStatus = (ids, status) =>
  unwrap(
    request("/libraries/facilities/status", {
      method: "PATCH",
      body: JSON.stringify({ ids, status }),
    }),
  );

export const CARE_FACILITY_TYPES = [
  "Hospital",
  "SNF",
  "ALF",
  "Clinic",
  "Home Health",
  "Hospice",
  "Rehab",
  "Other",
];

/** Factory helpers for catalog CRUD */
function makeCatalogApi(basePath) {
  return {
    list: (params, fetchOptions) =>
      unwrap(request(`${basePath}${buildQuery(params)}`, fetchOptions)),
    get: (id) => unwrap(request(`${basePath}/${id}`)),
    create: (body) =>
      unwrap(request(basePath, { method: "POST", body: JSON.stringify(body) })),
    update: (id, body) =>
      unwrap(
        request(`${basePath}/${id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        }),
      ),
    remove: (id) =>
      unwrap(request(`${basePath}/${id}`, { method: "DELETE" })),
    updateStatus: (id, status) =>
      unwrap(
        request(`${basePath}/${id}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status }),
        }),
      ),
    bulkUpdateStatus: (ids, status) =>
      unwrap(
        request(`${basePath}/status`, {
          method: "PATCH",
          body: JSON.stringify({ ids, status }),
        }),
      ),
  };
}

export const insuranceApi = makeCatalogApi("/libraries/insurances");
export const referralApi = makeCatalogApi("/libraries/referrals");
export const pharmacyApi = makeCatalogApi("/libraries/pharmacies");
export const physicianDirectoryApi = makeCatalogApi("/libraries/physicians");
export const staffPayRateApi = makeCatalogApi("/libraries/pay-rates");
export const clinicalCosignerApi = makeCatalogApi("/libraries/cosigners");
export const carePlanTemplateApi = makeCatalogApi(
  "/libraries/care-plan-templates",
);

/** Care Plan Library section catalog + fixed categories */
export const getCarePlanLibraryMeta = () =>
  unwrap(request("/libraries/care-plan/meta"));

/** Resolve active co-signer mapping for a user or role (form creator / signer). */
export const resolveClinicalCosigner = (userIdOrParams) => {
  const params =
    typeof userIdOrParams === "object" && userIdOrParams != null
      ? userIdOrParams
      : { userId: userIdOrParams };
  return unwrap(
    request(`/libraries/cosigners/resolve${buildQuery(params)}`),
  );
};

export const STAFF_PAY_VISIT_NOTES = [
  { value: "SOC", label: "SOC" },
  { value: "EVALUATION", label: "EVALUATION" },
  { value: "FOLLOW_UP", label: "FOLLOW UP" },
  { value: "REGULAR_DC", label: "REGULAR DC" },
  { value: "OASIS_DC", label: "OASIS DC" },
  { value: "ROC", label: "ROC" },
  { value: "REASSESSMENT", label: "REASSESSMENT" },
  { value: "ROUTINE", label: "ROUTINE" },
  { value: "PRN", label: "PRN" },
  { value: "SUPERVISORY", label: "SUPERVISORY" },
  { value: "RECERT", label: "RECERT" },
];

export const listInsurances = insuranceApi.list;
export const createInsurance = insuranceApi.create;
export const updateInsurance = insuranceApi.update;
export const deleteInsurance = insuranceApi.remove;
export const updateInsuranceStatus = insuranceApi.updateStatus;
export const bulkUpdateInsuranceStatus = insuranceApi.bulkUpdateStatus;

export const listReferrals = referralApi.list;
export const createReferral = referralApi.create;
export const updateReferral = referralApi.update;
export const deleteReferral = referralApi.remove;
export const updateReferralStatus = referralApi.updateStatus;
export const bulkUpdateReferralStatus = referralApi.bulkUpdateStatus;

export const listPharmacies = pharmacyApi.list;
export const createPharmacy = pharmacyApi.create;
export const updatePharmacy = pharmacyApi.update;
export const deletePharmacy = pharmacyApi.remove;
export const updatePharmacyStatus = pharmacyApi.updateStatus;
export const bulkUpdatePharmacyStatus = pharmacyApi.bulkUpdateStatus;

export const listPhysicianDirectory = physicianDirectoryApi.list;
export const createPhysicianDirectory = physicianDirectoryApi.create;
export const updatePhysicianDirectory = physicianDirectoryApi.update;
export const deletePhysicianDirectory = physicianDirectoryApi.remove;
export const updatePhysicianDirectoryStatus = physicianDirectoryApi.updateStatus;
export const bulkUpdatePhysicianDirectoryStatus =
  physicianDirectoryApi.bulkUpdateStatus;

export const PHARMACY_DIRECTORY_TYPES = [
  "Retail",
  "Mail Order",
  "Specialty",
  "Long-term Care",
  "Compounding",
  "Hospital",
  "Other",
];

export const PECOS_STATUS_OPTIONS = [
  { value: "enrolled", label: "Enrolled" },
  { value: "un-enrolled", label: "Un-enrolled" },
];
